import React, { useEffect, useRef, useMemo, lazy, Suspense, useCallback, useState } from 'react';
import 'react-quill/dist/quill.snow.css';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../context/ToastContext';
import { LatteSpinner } from '../common';
import ImageSettingsModal from './ImageSettingsModal';
import '../../styles/common/latte-components.css';

// ReactQuill을 동적으로 import하여 SSR 이슈 방지
const ReactQuill = lazy(() => import('react-quill'));

// Quill에 이미지 스타일 속성 추가
if (typeof window !== 'undefined') {
  const Quill = require('react-quill').Quill;
  const ImageFormatAttributesList = [
    'alt',
    'height',
    'width',
    'style'
  ];

  const BaseImageFormat = Quill.import('formats/image');
  class ImageFormat extends BaseImageFormat {
    static formats(node) {
      return ImageFormatAttributesList.reduce(function(formats, attribute) {
        if (node.hasAttribute(attribute)) {
          formats[attribute] = node.getAttribute(attribute);
        }
        return formats;
      }, {});
    }
    format(name, value) {
      if (ImageFormatAttributesList.indexOf(name) > -1) {
        if (value) {
          this.domNode.setAttribute(name, value);
        } else {
          this.domNode.removeAttribute(name);
        }
      } else {
        super.format(name, value);
      }
    }
  }

  Quill.register(ImageFormat, true);
}

const BlogEditor = ({ value, onChange, placeholder = "내용을 입력하세요...", onImageUploaded, onImageUploadStatusChange }) => {
  const quillRef = useRef(null);
  const { showError } = useToast();
  const processingImages = useRef(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // base64 이미지를 찾아서 업로드하는 함수
  const uploadBase64Images = useCallback(async () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const uploadPromises = [];
    const replacements = new Map();

    // 에디터의 현재 HTML 가져오기
    const currentHtml = editor.root.innerHTML;
    console.log('Checking for base64 images in editor...');
    
    // HTML에서 모든 base64 이미지 찾기
    const imgRegex = /<img[^>]+src="(data:image\/[^"]+)"[^>]*>/g;
    let match;
    let foundCount = 0;
    
    while ((match = imgRegex.exec(currentHtml)) !== null) {
      foundCount++;
      const base64Src = match[1];
      console.log(`Found base64 image ${foundCount}, length: ${base64Src.length}`);
      
      if (!processingImages.current.has(base64Src)) {
        processingImages.current.add(base64Src);
        
        const uploadPromise = (async () => {
          try {
            // base64를 Blob으로 변환
            const response = await fetch(base64Src);
            const blob = await response.blob();
            
            // 파일 확장자 결정
            const mimeType = blob.type || 'image/png';
            const ext = mimeType.split('/')[1] || 'png';
            const timestamp = Date.now();
            const randomStr = Math.random().toString(36).substring(7);
            const fileName = `blog-images/${timestamp}-${randomStr}.${ext}`;
            
            console.log(`Uploading image ${foundCount}: ${fileName}`);
            
            // Supabase에 업로드
            const { data, error } = await supabase.storage
              .from('portfolios')
              .upload(fileName, blob);

            if (error) throw error;

            // 공개 URL 가져오기
            const { data: { publicUrl } } = supabase.storage
              .from('portfolios')
              .getPublicUrl(fileName);

            console.log(`Successfully uploaded image ${foundCount}: ${publicUrl}`);
            
            // 교체 정보 저장
            replacements.set(base64Src, publicUrl);
            
            // 콜백 실행
            if (onImageUploaded) {
              onImageUploaded(publicUrl);
            }
            
            return publicUrl;
          } catch (error) {
            console.error(`Error uploading image ${foundCount}:`, error);
            showError('이미지 업로드에 실패했습니다');
            return null;
          } finally {
            processingImages.current.delete(base64Src);
          }
        })();
        
        uploadPromises.push(uploadPromise);
      }
    }

    // 모든 업로드가 완료될 때까지 대기
    if (uploadPromises.length > 0) {
      console.log(`Starting upload of ${uploadPromises.length} images...`);
      setIsUploading(true);
      if (onImageUploadStatusChange) {
        onImageUploadStatusChange(true);
      }
      
      await Promise.all(uploadPromises);
      
      console.log('All uploads completed. Replacements:', replacements.size);
      
      // HTML에서 모든 base64 이미지를 업로드된 URL로 교체
      let updatedHtml = currentHtml;
      replacements.forEach((newUrl, oldUrl) => {
        if (newUrl) {
          // 특수 문자 이스케이프
          const escapedOldUrl = oldUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          updatedHtml = updatedHtml.replace(new RegExp(escapedOldUrl, 'g'), newUrl);
        }
      });
      
      // 에디터에 업데이트된 HTML 설정
      if (updatedHtml !== currentHtml) {
        console.log('Updating editor with new URLs...');
        editor.root.innerHTML = updatedHtml;
        // onChange 콜백 호출
        onChange(updatedHtml);
      }
      
      setIsUploading(false);
      if (onImageUploadStatusChange) {
        onImageUploadStatusChange(false);
      }
    } else {
      console.log('No base64 images found.');
    }
  }, [onChange, onImageUploaded, showError, onImageUploadStatusChange]);

  // 이미지 파일 업로드 함수
  const uploadImageFile = useCallback(async (file) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const range = editor.getSelection(true);
    
    // 로딩 표시
    editor.insertText(range.index, '이미지 업로드 중...');
    
    try {
      const fileExt = file.name.split('.').pop();
      const originalName = file.name.substring(0, file.name.lastIndexOf('.'));
      const safeName = originalName.replace(/[^a-zA-Z0-9가-힣\-_]/g, '');
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(7);
      const fileName = `blog-images/${safeName}_${timestamp}-${randomStr}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('portfolios')
        .upload(fileName, file);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('portfolios')
        .getPublicUrl(fileName);

      // 로딩 텍스트 제거하고 이미지 삽입
      editor.deleteText(range.index, 15);
      editor.insertEmbed(range.index, 'image', publicUrl);
      
      if (onImageUploaded) {
        setTimeout(() => {
          onImageUploaded(publicUrl);
        }, 100);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      showError('이미지 업로드에 실패했습니다');
      editor.deleteText(range.index, 15);
    }
  }, [onImageUploaded, showError]);

  // 에디터 내용이 변경될 때마다 base64 이미지 체크
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      let timeoutId = null;
      
      const handleTextChange = () => {
        // 이전 타임아웃 취소
        if (timeoutId) clearTimeout(timeoutId);
        
        // 약간의 지연을 두고 base64 이미지 체크
        timeoutId = setTimeout(() => {
          uploadBase64Images();
        }, 1000);
      };

      editor.on('text-change', handleTextChange);
      
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        editor.off('text-change', handleTextChange);
      };
    }
  }, [uploadBase64Images]);

  // 붙여넣기 이벤트 처리
  const handlePaste = useCallback((e) => {
    const clipboardData = e.clipboardData || window.clipboardData;
    const items = clipboardData.items;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault();
        
        const file = items[i].getAsFile();
        if (file) {
          // 파일 크기 체크
          if (file.size > 5 * 1024 * 1024) {
            showError('이미지 크기는 5MB 이하여야 합니다');
            return;
          }

          uploadImageFile(file);
        }
        return; // 이미지 처리 완료 후 종료
      }
    }
  }, [uploadImageFile, showError]);

  // 이미지 핸들러
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        // 파일 크기 체크 (5MB)
        if (file.size > 5 * 1024 * 1024) {
          showError('이미지 크기는 5MB 이하여야 합니다');
          return;
        }

        uploadImageFile(file);
      }
    };
  }, [uploadImageFile, showError]);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchVisual: false
    }
  }), [imageHandler]);

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  // 경고 메시지 억제를 위한 useEffect
  useEffect(() => {
    // findDOMNode 경고 억제
    const originalError = console.error;
    const originalWarn = console.warn;
    
    console.error = (...args) => {
      if (
        typeof args[0] === 'string' && 
        (args[0].includes('findDOMNode') || args[0].includes('DOMNodeInserted'))
      ) {
        return;
      }
      originalError.apply(console, args);
    };
    
    console.warn = (...args) => {
      if (
        typeof args[0] === 'string' && 
        (args[0].includes('findDOMNode') || args[0].includes('DOMNodeInserted'))
      ) {
        return;
      }
      originalWarn.apply(console, args);
    };

    return () => {
      console.error = originalError;
      console.warn = originalWarn;
    };
  }, []);

  // 에디터 마운트 시 이벤트 리스너 추가
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const editorContainer = editor.root;
      
      // 붙여넣기 이벤트
      editorContainer.addEventListener('paste', handlePaste);
      
      // 이미지 클릭 이벤트로 모달 표시
      const handleImageClick = (e) => {
        if (e.target && e.target.tagName === 'IMG') {
          e.preventDefault();
          e.stopPropagation();
          setSelectedImage(e.target);
          setShowImageModal(true);
        }
      };
      
      editorContainer.addEventListener('click', handleImageClick);
      
      return () => {
        editorContainer.removeEventListener('paste', handlePaste);
        editorContainer.removeEventListener('click', handleImageClick);
      };
    }
  }, [handlePaste]);

  // 이미지 설정 저장
  const handleImageSettingsSave = useCallback((settings) => {
    if (!selectedImage) return;
    
    const { width, height, alignment } = settings;
    
    // 크기 설정
    selectedImage.setAttribute('width', width);
    selectedImage.setAttribute('height', height);
    selectedImage.style.width = `${width}px`;
    selectedImage.style.height = `${height}px`;
    
    // 기존 스타일 초기화
    selectedImage.style.display = '';
    selectedImage.style.margin = '';
    selectedImage.style.float = '';
    
    // 정렬 설정
    switch(alignment) {
      case 'left':
        selectedImage.style.float = 'left';
        selectedImage.style.margin = '10px 20px 10px 0';
        break;
      case 'center':
        selectedImage.style.display = 'block';
        selectedImage.style.margin = '0 auto';
        break;
      case 'right':
        selectedImage.style.float = 'right';
        selectedImage.style.margin = '10px 0 10px 20px';
        break;
      case 'none':
      default:
        // 모든 스타일 제거
        break;
    }
    
    // 변경사항을 에디터에 반영
    const editor = quillRef.current.getEditor();
    onChange(editor.root.innerHTML);
    
    // 모달 닫기
    setShowImageModal(false);
    setSelectedImage(null);
  }, [selectedImage, onChange]);

  return (
    <div className="blog-editor-container">
      <Suspense fallback={
        <div style={{ 
          minHeight: '300px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: 'var(--color-gray-50)',
          borderRadius: 'var(--border-radius-md)',
          border: '1px solid var(--color-gray-300)'
        }}>
          <LatteSpinner size="md" />
        </div>
      }>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          className="blog-editor"
        />
      </Suspense>
      
      {/* 이미지 설정 모달 */}
      <ImageSettingsModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setSelectedImage(null);
        }}
        image={selectedImage}
        onSave={handleImageSettingsSave}
      />
    </div>
  );
};

export default BlogEditor;
